export interface StartFailure {
  /** Stable code for the postMessage bridge. */
  code: string;
  /** Actionable, user-facing message for the error panel. */
  message: string;
}

/**
 * Decide whether the player can even start, before probing backends meaningfully. An insecure
 * (HTTP) origin hides navigator.xr/mediaDevices, so backend support looks empty — that must surface
 * as the actionable INSECURE_CONTEXT message, not a generic "unsupported device". Returns the
 * failure to show, or null when startup may proceed.
 */
export function evaluateStartupGate(env: {
  isSecureContext: boolean;
  supportedCount: number;
}): StartFailure | null {
  if (!env.isSecureContext) {
    // Reuse the classifier so the INSECURE_CONTEXT message has a single source of truth.
    return classifyStartError(new DOMException("insecure context", "SecurityError"));
  }
  if (env.supportedCount === 0) {
    return { code: "UNSUPPORTED", message: "This device doesn't support the AR experience." };
  }
  return null;
}

/**
 * Translate a backend start() failure into an actionable code + message. getUserMedia rejects with
 * DOMExceptions whose `name` pinpoints the cause; map the common ones so the user (and the host) get
 * a specific reason rather than a generic failure.
 */
export function classifyStartError(error: unknown): StartFailure {
  const name = error instanceof DOMException ? error.name : undefined;

  switch (name) {
    case "NotAllowedError":
      return { code: "CAMERA_DENIED", message: "Camera access was denied. Allow it and try again." };
    case "NotFoundError":
    case "OverconstrainedError":
      return { code: "NO_CAMERA", message: "No suitable camera was found on this device." };
    case "NotReadableError":
      return { code: "CAMERA_BUSY", message: "The camera is in use by another app. Close it and try again." };
    case "SecurityError":
      return { code: "INSECURE_CONTEXT", message: "AR requires a secure (https) connection." };
    default:
      return { code: "START_FAILED", message: "Couldn't start AR. Check camera and motion permissions." };
  }
}
